import { Link } from 'react-router-dom'
import type { UseMutationResult } from '@tanstack/react-query'
import {
  ArrowRight,
  AlertTriangle,
  Briefcase,
  CalendarDays,
  ClipboardList,
  Lock,
  MapPin,
  MessageCircle,
  Repeat,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { JobListActionsMenu } from '@/components/jobs/job-list-actions-menu'
import { JobOverdueBadge } from '@/components/jobs/job-overdue-badge'
import { JobTitleLink } from '@/components/jobs/job-title-link'
import {
  formatJobAmount,
  getJobBudgetOverrun,
  getJobCategoryColor,
  getJobClientName,
  getJobSpendSummary,
  getJobWorkerInitials,
  getRecentJobLabel,
  getRecurrenceLabel,
  getRecurrenceOccurrenceHint,
  isNewJob,
  JOB_LOCKED_BADGE_CLASS,
  jobPriorityColors,
  jobPriorityLabels,
  jobScheduleStateBadgeClasses,
  jobScheduleStateCardClasses,
  jobScheduleStateLabels,
  jobStatusColors,
  resolveJobCategoryKey,
  resolveJobScheduleState,
} from '@/lib/job-list-display'
import { withServiceInstanceId } from '@/lib/service-instance'
import { withReturnTo } from '@/lib/return-navigation'
import { cn, formatCurrency } from '@/lib/utils'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import type { Job } from '@/types'

type JobListCardProps = {
  job: Job
  serviceInstanceId?: string | null
  assignmentReturnPath?: string | null
  relativeTimeNow: number
  onOpen: () => void
  onChat?: () => void
  onDelete: () => void
  onOrderSelect?: (orderReferenceId: string) => void
  onStatusApplied?: () => void
  lockMutation: UseMutationResult<unknown, unknown, { id: string; lock: boolean }, unknown>
}

const compactBadgeClass = 'h-4 px-1.5 py-0 text-[9px] font-medium leading-none'

const formatJobScheduleDateTimeLabel = (value?: string | null): string => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (!Number.isFinite(parsed.getTime())) {
    return '-'
  }

  const dateLabel = parsed.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const timeLabel = parsed.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${dateLabel}, ${timeLabel}`
}

export function JobListCard({
  job,
  serviceInstanceId,
  assignmentReturnPath,
  relativeTimeNow,
  onOpen,
  onChat,
  onDelete,
  onOrderSelect,
  onStatusApplied,
  lockMutation,
}: JobListCardProps) {
  const labels = useWorkspaceLabels()
  const recentLabel = getRecentJobLabel(job.created_at, relativeTimeNow)
  const showNewBadge = isNewJob(job.created_at, relativeTimeNow)
  const recurrenceLabel = getRecurrenceLabel(job)
  const recurrenceHint = getRecurrenceOccurrenceHint(job)
  const clientName = getJobClientName(job)
  const workerName = job.primary_worker_info?.name
  const startLabel = formatJobScheduleDateTimeLabel(job.start_date ?? job.scheduled_start)
  const endLabel = formatJobScheduleDateTimeLabel(job.end_date ?? job.scheduled_end)
  const locationLabel = job.location || job.address || 'No location'
  const statusKey = job.status || 'draft'
  const scheduleState = resolveJobScheduleState(job, relativeTimeNow)
  const categoryKey = resolveJobCategoryKey(job)
  const categoryColor = getJobCategoryColor(categoryKey)
  const clashCount = Number(job.workday_entry_clash_count ?? 0)
  const budgetOverrun = getJobBudgetOverrun(job)
  const spendSummary = getJobSpendSummary(job)

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen()
        }
      }}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-xl',
        jobScheduleStateCardClasses[scheduleState],
        'shadow-sm transition duration-150',
        'hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
      )}
    >
      <div className={cn('h-1 w-full bg-gradient-to-r', categoryColor.accent)} />

      {onChat ? (
        <button
          type="button"
          data-row-action="true"
          aria-label={`Open chat for ${job.title}`}
          className="absolute right-2 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background/95 text-primary shadow-sm transition hover:bg-primary hover:text-primary-foreground"
          onClick={(event) => {
            event.stopPropagation()
            onChat()
          }}
        >
          <MessageCircle className="h-4 w-4" />
        </button>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col p-2.5">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {job.category_name ? (
                <Badge variant="outline" className={cn(compactBadgeClass, categoryColor.badge)}>
                  {job.category_name}
                </Badge>
              ) : null}
              {scheduleState === 'overdue' ? (
                <JobOverdueBadge className={compactBadgeClass} />
              ) : scheduleState !== 'none' ? (
                <Badge variant="outline" className={cn(compactBadgeClass, jobScheduleStateBadgeClasses[scheduleState])}>
                  {jobScheduleStateLabels[scheduleState]}
                </Badge>
              ) : null}
              <Badge variant="outline" className={cn(compactBadgeClass, jobStatusColors[statusKey] || jobStatusColors.draft)}>
                {job.status.replace(/_/g, ' ')}
              </Badge>
              <Badge variant="outline" className={cn(compactBadgeClass, jobPriorityColors[job.priority] || jobPriorityColors.medium)}>
                {jobPriorityLabels[job.priority] || job.priority}
              </Badge>
              {showNewBadge ? (
                <span className="inline-flex h-4 items-center rounded-full bg-emerald-100 px-1.5 text-[9px] font-semibold text-emerald-700">
                  New
                </span>
              ) : null}
              {job.is_locked ? (
                <Badge variant="outline" className={cn(JOB_LOCKED_BADGE_CLASS, compactBadgeClass)}>
                  <Lock className="h-2.5 w-2.5" />
                  Locked
                </Badge>
              ) : null}
              {clashCount > 0 ? (
                <Badge variant="outline" className={cn(compactBadgeClass, 'gap-0.5 border-amber-300 bg-amber-50 text-amber-800')}>
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {clashCount} clash{clashCount === 1 ? '' : 'es'}
                </Badge>
              ) : null}
              {recurrenceLabel ? (
                <Badge variant="secondary" className={cn(compactBadgeClass, 'gap-0.5')}>
                  <Repeat className="h-2.5 w-2.5" />
                  {recurrenceLabel}
                </Badge>
              ) : null}
            </div>

            {recurrenceHint && recurrenceHint !== recurrenceLabel ? (
              <p className="mb-1 line-clamp-1 text-[10px] text-violet-700">{recurrenceHint}</p>
            ) : null}

            <h3 className="line-clamp-1 text-[13px] font-semibold leading-tight">
              <JobTitleLink
                jobId={job.id}
                title={job.title}
                jobRef={job.job_ref}
                entityLabel={labels.entity}
                detailBasePath={labels.basePath}
                serviceInstanceId={serviceInstanceId}
                returnPath={assignmentReturnPath}
                className="text-foreground hover:text-primary"
                onClick={(event) => event.stopPropagation()}
              />
            </h3>

            <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[10px] text-muted-foreground">
              <span className="inline-flex items-center gap-0.5 font-medium text-foreground/75">
                <Briefcase className="h-3 w-3" />
                {job.job_ref || `#${job.id}`}
              </span>
              {recentLabel ? <span>{recentLabel} ago</span> : null}
              {job.order_reference_id ? (
                <button
                  type="button"
                  data-row-action="true"
                  className="font-medium text-primary hover:underline"
                  onClick={(event) => {
                    event.stopPropagation()
                    onOrderSelect?.(job.order_reference_id!)
                  }}
                >
                  Ord {job.order_reference_id}
                </button>
              ) : null}
              {job.recurrence_parent_info ? (
                <span className="text-muted-foreground/80">Generated</span>
              ) : null}
            </p>
          </div>

          <div className="shrink-0 text-right" data-row-action="true" onClick={(event) => event.stopPropagation()}>
            <JobListActionsMenu
              job={job}
              serviceInstanceId={serviceInstanceId}
              assignmentReturnPath={assignmentReturnPath}
              onView={onOpen}
              onChat={onChat}
              onDelete={onDelete}
              onOrderSelect={onOrderSelect}
              onStatusApplied={onStatusApplied}
              lockMutation={lockMutation}
              triggerClassName="h-7 w-7 opacity-70 transition group-hover:opacity-100"
            />
            <div className="mt-1 space-y-1 text-right">
              <p className="text-sm font-semibold leading-none text-foreground">
                {formatJobAmount(job)}
              </p>
              <div className="space-y-0.5 text-[9px] font-normal leading-tight text-muted-foreground">
                <p>Est: {spendSummary.estimated === null ? '-' : formatCurrency(spendSummary.estimated)}</p>
                <p>Actual: {spendSummary.actual === null ? '-' : formatCurrency(spendSummary.actual)}</p>
              </div>
              {budgetOverrun ? (
                <Badge
                  variant="outline"
                  className="gap-0.5 border-rose-300 bg-rose-50 px-1.5 py-0 text-[9px] font-medium text-rose-700"
                  title={`Actual spent ${formatCurrency(budgetOverrun.expenses)} exceeds estimated spent ${formatCurrency(budgetOverrun.budget)}`}
                >
                  <AlertTriangle className="h-2.5 w-2.5" />
                  +{formatCurrency(budgetOverrun.overBy)}
                </Badge>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-x-2.5 gap-y-1.5 border-t border-border/50 pt-2 text-[10px]">
          <div className="min-w-0">
            <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Client</p>
            <p className="truncate font-medium text-foreground">{clientName}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Worker</p>
            {job.primary_worker && workerName ? (
              <Link
                to={withServiceInstanceId(`/workers/${job.primary_worker}`, serviceInstanceId)}
                className="flex items-center gap-1 truncate font-medium text-primary hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[8px] font-semibold text-violet-800">
                  {getJobWorkerInitials(workerName)}
                </span>
                <span className="truncate">{workerName}</span>
              </Link>
            ) : (
              <p className="text-muted-foreground">Unassigned</p>
            )}
          </div>
          <div className="col-span-2 min-w-0">
            <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Location</p>
            <p className="flex items-start gap-1 line-clamp-1 text-foreground/90">
              <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{locationLabel}</span>
            </p>
          </div>
          <div>
            <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Start</p>
            <p className="font-medium text-foreground">{startLabel}</p>
          </div>
          <div>
            <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">End</p>
            <p className="font-medium text-foreground">{endLabel}</p>
          </div>
        </div>

        <div
          className="mt-2 flex items-center justify-between gap-1.5 border-t border-border/50 pt-2"
          data-row-action="true"
        >
          <Link
            to={withReturnTo(withServiceInstanceId(`/assignments?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), assignmentReturnPath)}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            <ClipboardList className="h-3 w-3" />
            Assignments
          </Link>

          <div className="flex items-center gap-1">
            {onChat ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 rounded-md px-2 text-[10px]"
                onClick={(event) => {
                  event.stopPropagation()
                  onChat()
                }}
              >
                <MessageCircle className="mr-0.5 h-3 w-3" />
                Chat
              </Button>
            ) : null}
            <Button
              asChild
              variant="outline"
              size="sm"
              className="h-6 rounded-md px-2 text-[10px]"
              onClick={(event) => event.stopPropagation()}
            >
              <Link to={withReturnTo(withServiceInstanceId(`${labels.basePath}/workdays?job=${encodeURIComponent(String(job.id))}`, serviceInstanceId), assignmentReturnPath)}>
                <CalendarDays className="mr-0.5 h-3 w-3" />
                {labels.workDayPlural}
              </Link>
            </Button>
            <Button
              size="sm"
              className="h-6 rounded-md px-2 text-[10px]"
              onClick={(event) => {
                event.stopPropagation()
                onOpen()
              }}
            >
              Open
              <ArrowRight className="ml-0.5 h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </article>
  )
}

export function JobListCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-card/90">
      <div className="h-1 bg-muted" />
      <div className="space-y-2 p-2.5">
        <div className="flex gap-2">
          <div className="flex-1 space-y-1.5">
            <div className="h-4 w-20 rounded bg-muted" />
            <div className="h-3.5 w-full rounded bg-muted" />
            <div className="h-3 w-2/3 rounded bg-muted" />
          </div>
          <div className="h-4 w-12 rounded bg-muted" />
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-border/50 pt-2">
          <div className="h-6 rounded bg-muted/60" />
          <div className="h-6 rounded bg-muted/60" />
          <div className="col-span-2 h-6 rounded bg-muted/60" />
        </div>
        <div className="h-6 rounded bg-muted/60" />
      </div>
    </div>
  )
}
